import React, { useState } from "react";
import './EditDOB.css';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { useSelector, useDispatch } from "react-redux";
import Loader from "../Loader";
import { updateUser } from "../../api/auth";
import { getToken } from "../../api/index";
import { Snackbar, Alert } from '@mui/material';
import { setUserDetails } from "../../Redux/Slices/userDetailsSlice";

function EditDOB() {
    const maxDate = dayjs().subtract(18, 'year'); // Must be at least 18 years old
    const minDate = dayjs().subtract(100, 'year'); // Maximum age of 100 years
    const dispatch = useDispatch();
    const userDetails = useSelector((state) => state.userDetails.userDetails);
    const [date, setDate] = useState(userDetails?.dateOfBirth ? userDetails.dateOfBirth.split('T')[0] : "");
    const [isLoading, setIsLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar({ ...snackbar, open: false });
    };

    const showSnackbar = (message, severity) => {
        setSnackbar({
            open: true,
            message,
            severity
        });
    };

    const handleSubmit = async () => {
        if (isLoading) return;

        if (!date) {
            showSnackbar('Please select a date of birth', 'error');
            return;
        }

        setIsLoading(true);
        const token = getToken();
        const payload = {
            userId: userDetails.userId,
            dateOfBirth: date,
        };

        try {
            const response = await updateUser(payload, token);
            setIsLoading(false);

            if (response && response.success !== false) {
                showSnackbar('Date of birth updated successfully!', 'success');
                dispatch(setUserDetails({ ...userDetails, dateOfBirth: date }));
            } else {
                showSnackbar('Failed to update date of birth. Please try again.', 'error');
            }
        } catch (error) {
            console.error("error", error);
            setIsLoading(false);
            showSnackbar('An error occurred while updating. Please try again.', 'error');
        }
    };

    return (
        <div className="EditDOB">
            <div className="editDOBContainer">
                <div className="editDOBCard">
                    <span className="editDOBLabel">Date of birth</span>
                    <div style={{ width: "100%" }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                value={date ? dayjs(date) : null}
                                onChange={(newValue) => {
                                    if (newValue) {
                                        setDate(newValue.format('YYYY-MM-DD'));
                                    }
                                }}
                                maxDate={maxDate}
                                minDate={minDate}
                                disableFuture
                                slotProps={{
                                    textField: {
                                        placeholder: 'Select Date of Birth',
                                        error: Boolean(date && (dayjs(date).isAfter(maxDate) || dayjs(date).isBefore(minDate))),
                                        helperText: date && (dayjs(date).isAfter(maxDate) ? 'Must be at least 18 years old' :
                                            dayjs(date).isBefore(minDate) ? 'Invalid date of birth' : ''),
                                        sx: {
                                            width: '100%',
                                            '& .MuiOutlinedInput-root': {
                                                backgroundColor: '#142424',
                                                borderRadius: '12px',
                                                color: '#FFFFFF',
                                                fontSize: '15px',
                                                fontFamily: 'Inter, sans-serif',
                                                height: '52px',
                                                paddingRight: '12px',
                                                transition: 'all 0.2s ease',
                                                '& .MuiOutlinedInput-notchedOutline': {
                                                    border: '1px solid #233737 !important',
                                                    borderRadius: '12px !important',
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                    border: '1px solid #4DCBCA !important',
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    border: '1.5px solid #5AAF9A !important',
                                                },
                                                '&.Mui-focused': {
                                                    boxShadow: '0 0 0 3px rgba(90, 175, 154, 0.2)',
                                                    outline: 'none !important',
                                                },
                                            },
                                            '& .MuiInputBase-input, & input, & .MuiPickersSectionList-root, & .MuiPickersSectionList-sectionContent': {
                                                color: '#FFFFFF !important',
                                                WebkitTextFillColor: '#FFFFFF !important',
                                                opacity: 1,
                                                letterSpacing: 'normal !important',
                                                wordSpacing: 'normal !important',
                                                fontSize: '15px',
                                                fontFamily: 'Inter, sans-serif',
                                                fontWeight: 500,
                                            },
                                            '& .MuiInputBase-input': {
                                                padding: '0 18px',
                                                height: '100%',
                                                outline: 'none !important',
                                                border: 'none !important',
                                                '&::placeholder': {
                                                    color: '#7A7A7A',
                                                    opacity: 1,
                                                },
                                            },
                                            '& .MuiIconButton-root': {
                                                color: '#5AAF9A',
                                                padding: '8px',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(90, 175, 154, 0.15)',
                                                },
                                            },
                                            '& .MuiFormHelperText-root': {
                                                color: '#ff5252',
                                                fontSize: '12px',
                                                fontFamily: 'Inter, sans-serif',
                                                marginTop: '6px',
                                                marginLeft: '16px',
                                            },
                                        },
                                    },
                                    popper: {
                                        sx: {
                                            '& .MuiPaper-root': {
                                                backgroundColor: '#0E1B1B',
                                                color: '#FFFFFF',
                                                borderRadius: '16px',
                                                border: '1px solid #1E2D2D',
                                                boxShadow: '0 12px 36px rgba(0, 0, 0, 0.7)',
                                                padding: '12px',
                                                fontFamily: 'Inter, sans-serif',
                                            },
                                            '& .MuiPickersCalendarHeader-root': {
                                                color: '#FFFFFF',
                                                paddingLeft: '12px',
                                                paddingRight: '12px',
                                            },
                                            '& .MuiPickersCalendarHeader-label': {
                                                color: '#FFFFFF',
                                                fontWeight: '600',
                                                fontSize: '15px',
                                                fontFamily: 'Inter, sans-serif',
                                            },
                                            '& .MuiPickersCalendarHeader-switchViewButton': {
                                                color: '#5AAF9A',
                                                '&:hover': {
                                                    backgroundColor: '#142424',
                                                },
                                            },
                                            '& .MuiPickersArrowSwitcher-button': {
                                                color: '#5AAF9A',
                                                '&:hover': {
                                                    backgroundColor: '#142424',
                                                },
                                            },
                                            '& .MuiDayCalendar-weekDayLabel': {
                                                color: '#839696',
                                                fontWeight: '600',
                                                fontSize: '13px',
                                                fontFamily: 'Inter, sans-serif',
                                            },
                                            '& .MuiPickersDay-root': {
                                                color: '#FFFFFF',
                                                fontSize: '14px',
                                                fontFamily: 'Inter, sans-serif',
                                                margin: '2px',
                                                borderRadius: '8px',
                                                transition: 'all 0.15s ease',
                                                '&:hover': {
                                                    backgroundColor: '#142424',
                                                    color: '#4DCBCA',
                                                },
                                                '&.Mui-selected': {
                                                    backgroundColor: '#5AAF9A !important',
                                                    color: '#0E1B1B !important',
                                                    fontWeight: '700',
                                                    '&:hover': {
                                                        backgroundColor: '#4DCBCA !important',
                                                    },
                                                },
                                                '&.MuiPickersDay-today': {
                                                    border: '1px solid #5AAF9A !important',
                                                },
                                                '&.Mui-disabled': {
                                                    color: 'rgba(255, 255, 255, 0.25) !important',
                                                },
                                            },
                                            '& .MuiPickersYear-yearButton': {
                                                color: '#FFFFFF',
                                                borderRadius: '8px',
                                                fontFamily: 'Inter, sans-serif',
                                                '&:hover': {
                                                    backgroundColor: '#142424',
                                                    color: '#4DCBCA',
                                                },
                                                '&.Mui-selected': {
                                                    backgroundColor: '#5AAF9A !important',
                                                    color: '#0E1B1B !important',
                                                    fontWeight: '700',
                                                },
                                            },
                                            '& .MuiMonthCalendar-root .MuiPickersMonth-monthButton': {
                                                color: '#FFFFFF',
                                                borderRadius: '8px',
                                                fontFamily: 'Inter, sans-serif',
                                                '&:hover': {
                                                    backgroundColor: '#142424',
                                                    color: '#4DCBCA',
                                                },
                                                '&.Mui-selected': {
                                                    backgroundColor: '#5AAF9A !important',
                                                    color: '#0E1B1B !important',
                                                    fontWeight: '700',
                                                },
                                            },
                                        },
                                    },
                                }}
                            />
                        </LocalizationProvider>
                    </div>
                </div>
                <div className="editDOBSubmitBtnContainer">
                    <button
                        className="editDOBSubmitBtn"
                        onClick={handleSubmit}
                        disabled={isLoading}
                    >
                        {isLoading ? <Loader /> : "Update"}
                    </button>
                </div>
            </div>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
}

export default EditDOB;
